import { ApolloError } from '@apollo/client'
import { useLocation } from '@gatsbyjs/reach-router'
import * as R from 'ramda'
import { useCallback } from 'react'

import { SimpleProductCartItemInput } from '../../../../graphql/magento'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import type { Maybe } from '../../../../utils/types'
import { publishAddToCartEventForItems } from '../../../analytics/cart'
import { UseCartAddSimpleProducts, useCartAddSimpleProducts } from '../../../cart'
import { useAddToCartToast } from '../../../cart/hooks/useAddToCartToast'
import { droppedItemsGuidance } from '../../../escript/copy'
import { setEScriptDroppedSkus } from '../../../escript/state'
import { EScriptParamsLoader, EScriptUpdateCartAction } from '../../../escript/types'
import { isAddToCartAction } from '../../../escript/utils'
import { useTestKitProducts } from '../../../products/hooks/useTestKitProducts'
import { setDispensaryCartState } from '../../events/cart'
import { removeDispensaryPrefixAndSlug } from '../../utils/dispensaries'

export type UseAddProductsToCart = [
  /**
   * Resolves with what the add achieved, or `undefined` when the link asked for no add at all —
   * a distinction the caller needs, because only the former can be responsible for an empty cart.
   */
  addProductsToCart: () => Promise<AddToCartResult | undefined>,
  result: UseCartAddSimpleProducts['1'],
]

export interface UseAddProductsToCartOptions {
  eScriptParamsLoader: EScriptParamsLoader
}

const shouldAddToCart = (
  updateCartActions: Maybe<EScriptUpdateCartAction[]>,
): updateCartActions is EScriptUpdateCartAction[] => {
  return Boolean(
    updateCartActions && updateCartActions.some((action) => action?.operation === 'add'),
  )
}

const getProducts = (updateCartActions: EScriptUpdateCartAction[]) => {
  return updateCartActions
    .filter(isAddToCartAction)
    .map(({ products }) => products)
    .flat()
}

export interface AddToCartResult {
  itemsAdded: SimpleProductCartItemInput[]
  itemsFailed: SimpleProductCartItemInput[]
}

/**
 * Whether the hydration is landing on checkout, the one place `EScriptCartAlerts` renders — so the
 * toast would only repeat what the durable notice already says.
 *
 * A path test rather than `InCheckoutContext`: this hook is mounted by `DispensaryPageEffects`, which
 * wraps the page, while the context's provider (`SharedBuyLayout`) sits inside it, so the context
 * always reads `false` from here. `removeDispensaryPrefixAndSlug` drops the `/u/{slug}` prefix, the
 * same normalisation `usePersistentToast` applies before its own `/^\/checkout/` exclusion.
 *
 * Reach-router's `useLocation` rather than `useLocationContext`: this is the closure case that hook's
 * docblock warns about. The hydration captures the path on the render that starts it, so a client-side
 * arrival at checkout would decide against the previous page's path and never see the correction.
 */
const landsOnCheckout = (pathname: string): boolean =>
  /^\/checkout/.test(removeDispensaryPrefixAndSlug(pathname))

const extractFailedSku = (message: string): string | undefined => {
  const regex1 = /Could not find a product with SKU "(.*)"/
  const regex2 = /Could not add the product with SKU ([^(*)]+) to the shopping cart/
  return regex1.exec(message)?.[1] ?? regex2.exec(message)?.[1] ?? undefined
}

const reduceAddToCartResult = (skuFailed: string) =>
  R.groupBy<SimpleProductCartItemInput, 'itemsFailed' | 'retryItems'>((item) =>
    item.data.sku === skuFailed ? 'itemsFailed' : 'retryItems',
  )

const publishAddToCartEvent =
  (affiliateId: string | undefined) =>
  ({ itemsAdded }: AddToCartResult) => {
    if (itemsAdded.length > 0) {
      publishAddToCartEventForItems({
        affiliation: affiliateId,
        items: itemsAdded.map(
          ({ data: { quantity, sku, dfh_autoship_interval: autoShipInterval } }) => ({
            quantity,
            sku,
            ...(autoShipInterval && { dfh_autoship_interval: autoShipInterval }),
          }),
        ),
        appContext: 'escript',
      })
    }
  }

export const useAddProductsToCart = ({
  eScriptParamsLoader,
}: UseAddProductsToCartOptions): UseAddProductsToCart => {
  const { loadParams } = eScriptParamsLoader

  const { isTestKitSku } = useTestKitProducts()
  const [addSimpleProductsToCart, result] = useCartAddSimpleProducts()
  const { error } = result

  const { showFailureToast } = useAddToCartToast()
  const location = useLocation()

  useLogAndCaptureError(error)

  const reportFailedItems = useCallback(
    (addResult: AddToCartResult) => {
      if (addResult.itemsFailed.length === 0) {
        // clear an earlier hydration's report, so the notice only ever describes the latest add
        setEScriptDroppedSkus([])
        return
      }
      const skus = addResult.itemsFailed.map((itemFailed) => itemFailed.data.sku)
      // the toast can be raised and dismissed while the checkout skeleton is still up, so the
      // shopper also needs a record of the dropped items that survives until they see the cart
      setEScriptDroppedSkus(skus)
      if (landsOnCheckout(location.pathname)) {
        return
      }
      // The only report off checkout: an eScript link can land on any dispensary page, and
      // `EScriptCartAlerts` renders on none of them. So it carries the same guidance the notice
      // would have, from the one place both read it.
      showFailureToast({ skus, additionalMessage: droppedItemsGuidance })
    },
    [location.pathname, showFailureToast],
  )

  const tryAddProductsToCart = useCallback(
    (
      addItems: SimpleProductCartItemInput[],
      itemsFailedAcc: SimpleProductCartItemInput[],
    ): Promise<AddToCartResult> =>
      addSimpleProductsToCart({
        variables: {
          cartItems: addItems,
        },
      })
        .then(() => ({ itemsAdded: addItems, itemsFailed: itemsFailedAcc }))
        .catch((addError: unknown) => {
          if (!(addError instanceof ApolloError) || addError.networkError) {
            return Promise.reject(addError)
          }
          const failedSku = extractFailedSku(addError.message)
          if (!failedSku) {
            return Promise.reject(addError)
          }
          const { retryItems, itemsFailed } = reduceAddToCartResult(failedSku)(addItems)
          const itemsFailedAccNew = [...itemsFailedAcc, ...itemsFailed]
          if (!retryItems || retryItems.length === 0) {
            return { itemsAdded: [], itemsFailed: itemsFailedAccNew }
          }
          return tryAddProductsToCart(retryItems, itemsFailedAccNew)
        }),
    [addSimpleProductsToCart],
  )

  const addProductsToCart = useCallback(async () => {
    const { updateCartActions, affiliateId } = await loadParams()

    if (!shouldAddToCart(updateCartActions)) {
      return
    }
    const products = getProducts(updateCartActions)
    if (!products) {
      return
    }
    const items = products.map((product) => ({
      data: {
        sku: product.productSku,
        quantity: product.productQuantity,
        ...(product?.productAutoShip &&
          !isTestKitSku(product.productSku) && {
            dfh_order_type: 2,
            dfh_autoship_interval: autoShipIntervalToAutoShipOption(product.productAutoShip),
          }),
      },
    }))

    const productsForState = products.map(({ productSku, productQuantity, productAutoShip }) => ({
      sku: productSku,
      quantity: productQuantity,
      ...(productAutoShip &&
        !isTestKitSku(productSku) && {
          dfh_autoship_interval: autoShipIntervalToAutoShipOption(productAutoShip),
        }),
    }))
    setDispensaryCartState({
      type: 'cart_add_products_start',
      products: productsForState,
    })
    return tryAddProductsToCart(items, [])
      .then(R.tap(reportFailedItems))
      .then(R.tap(publishAddToCartEvent(affiliateId)))
      .then(
        R.tap(() =>
          setDispensaryCartState({
            type: 'cart_add_products_complete',
            products: productsForState,
          }),
        ),
      )
  }, [isTestKitSku, loadParams, reportFailedItems, tryAddProductsToCart])

  return [addProductsToCart, result]
}

function autoShipIntervalToAutoShipOption(autoShipInterval: string): number {
  return parseInt(autoShipInterval, 10)
}
